import { dayjs } from "@/core/utils/date";
const DEFAULT_POLL_AFTER_MS = 1_500;
export const DEFAULT_MAX_POLL_INTERVAL_MS = 10_000;
export const DEFAULT_POLL_TIMEOUT_MS = 300_000;
const POLL_BACKOFF_MULTIPLIER = 1.5;

export interface PollingHints {
  poll_after_ms: number | null;
  max_poll_interval_ms: number | null;
  poll_timeout_ms: number | null;
}

export type PollingResult<T> = T & { polling_timed_out?: boolean };

export interface PollingRuntimeOptions {
  now?: () => number;
  wait?: (delayMs: number, signal: AbortSignal) => Promise<void>;
}

const abortError = () => {
  const error = new Error("Polling canceled");
  error.name = "AbortError";
  return error;
};

export const waitForPoll = (delayMs: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }

    const onAbort = () => {
      globalThis.clearTimeout(timeout);
      reject(abortError());
    };
    const timeout = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);

    signal.addEventListener("abort", onAbort, { once: true });
  });

export const pollStatus = async <T extends PollingHints>({
  initial,
  isPending,
  request,
  signal,
  now = () => dayjs().valueOf(),
  wait = waitForPoll,
  onUpdate,
}: PollingRuntimeOptions & {
  initial: T;
  isPending: (value: T) => boolean;
  request: (signal: AbortSignal) => Promise<T>;
  signal: AbortSignal;
  onUpdate?: (value: T) => void;
}): Promise<PollingResult<T>> => {
  let current = initial;
  let delayMs = current.poll_after_ms ?? DEFAULT_POLL_AFTER_MS;
  const startedAt = now();
  const timeoutMs = current.poll_timeout_ms ?? DEFAULT_POLL_TIMEOUT_MS;
  const controller = new AbortController();
  let timedOut = false;
  const abortPolling = () => controller.abort();
  const deadline = globalThis.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  if (signal.aborted) controller.abort();
  signal.addEventListener("abort", abortPolling, { once: true });

  try {
    while (isPending(current)) {
      const remainingMs = timeoutMs - (now() - startedAt);
      if (remainingMs <= 0) {
        return { ...current, polling_timed_out: true };
      }

      await wait(Math.min(delayMs, remainingMs), controller.signal);
      if (signal.aborted) throw abortError();
      if (now() - startedAt >= timeoutMs) {
        return { ...current, polling_timed_out: true };
      }

      current = await request(controller.signal);
      onUpdate?.(current);

      const hintedDelay = current.poll_after_ms ?? delayMs;
      const maxDelay =
        current.max_poll_interval_ms ?? DEFAULT_MAX_POLL_INTERVAL_MS;
      delayMs = Math.min(
        maxDelay,
        Math.max(hintedDelay, Math.ceil(delayMs * POLL_BACKOFF_MULTIPLIER)),
      );
    }
  } catch (error) {
    if (timedOut) return { ...current, polling_timed_out: true };
    throw error;
  } finally {
    globalThis.clearTimeout(deadline);
    signal.removeEventListener("abort", abortPolling);
  }

  return current;
};
